import React, { useState, useEffect, useRef } from 'react';
import { useUserData } from '../context/UserDataContext';
import { appendHistorySnapshot } from '../lib/userData';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { CheckCircle, AlertTriangle, XCircle, Plus, Trash2, X, Link2 } from 'lucide-react';
import AccountLinkModal, { ConnectionBadge, ManualAccountBanner } from './AccountLinkModal';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const PIE_COLORS = {
  Retirement: '#8b5cf6',
  Investment: '#3b82f6',
  Cash: '#10b981',
  'Real Estate': '#f59e0b',
};

// Maps a linked account's internal type to a NetWorth pie category
const LINKED_TYPE_TO_PIE = {
  checking:   'Cash',
  savings:    'Cash',
  brokerage:  'Investment',
  '401k':     'Retirement',
  roth_ira:   'Retirement',
  ira:        'Retirement',
  other:      'Cash',
};

const ESTATE_STATUS = {
  done:    { icon: <CheckCircle size={15} className="text-emerald-400" />, label: 'Current',  color: 'text-emerald-400' },
  warning: { icon: <AlertTriangle size={15} className="text-amber-400" />, label: 'Outdated', color: 'text-amber-400' },
  missing: { icon: <XCircle size={15} className="text-rose-400" />,        label: 'Missing',  color: 'text-rose-400' },
};

function groupByType(assets) {
  const groups = {};
  assets.forEach(a => {
    groups[a.type] = (groups[a.type] || 0) + a.value;
  });
  return Object.entries(groups).map(([name, value]) => ({ name, value }));
}

// -- Add Asset Modal -----------------------------------------------------------
function AddAssetModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [type, setType] = useState('Cash');

  const handleSubmit = (e) => {
    e.preventDefault();
    const v = parseFloat(value.replace(/[^0-9.]/g, ''));
    if (!name.trim() || isNaN(v) || v < 0) return;
    onAdd({ name: name.trim(), value: v, type });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-100 text-lg">Add Asset</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Account / Asset Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Fidelity 401k"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Current Value</label>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm font-semibold">$</span>
              <input
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder="0"
                type="number"
                min="0"
                step="0.01"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                required
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Asset Type</label>
            <select
              value={type}
              onChange={e => setType(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="Retirement">Retirement (401k, IRA, Roth)</option>
              <option value="Investment">Investment (Brokerage)</option>
              <option value="Cash">Cash (Savings, Checking, HYSA)</option>
              <option value="Real Estate">Real Estate</option>
            </select>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
              Add Asset
            </button>
            <button type="button" onClick={onClose} className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -- Add Liability Modal -------------------------------------------------------
function AddLiabilityModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  const [value, setValue] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const v = parseFloat(value.replace(/[^0-9.]/g, ''));
    if (!name.trim() || isNaN(v) || v < 0) return;
    onAdd({ name: name.trim(), value: v });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-100 text-lg">Add Liability</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Debt / Liability Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Mortgage, Student Loan"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Outstanding Balance</label>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm font-semibold">$</span>
              <input
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder="0"
                type="number"
                min="0"
                step="0.01"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
                required
              />
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" className="flex-1 bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
              Add Liability
            </button>
            <button type="button" onClick={onClose} className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -- Add Estate Item Modal -----------------------------------------------------
function AddEstateModal({ onClose, onAdd }) {
  const [item, setItem] = useState('');
  const [status, setStatus] = useState('missing');
  const [date, setDate] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!item.trim()) return;
    onAdd({ item: item.trim(), status, date: date || null });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-100 text-lg">Add Estate Document</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Document Name</label>
            <input
              value={item}
              onChange={e => setItem(e.target.value)}
              placeholder="e.g. Will, Living Trust, POA"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="done">Current / Up to date</option>
              <option value="warning">Outdated -- needs review</option>
              <option value="missing">Missing -- not created</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Date (optional)</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
              Add Document
            </button>
            <button type="button" onClick={onClose} className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -- Main NetWorth component ---------------------------------------------------
export default function NetWorth() {
  const { netWorthData, updateSection, linkedAccountsData, userProfile } = useUserData();

  const [showAddAsset,     setShowAddAsset]     = useState(false);
  const [showAddLiability, setShowAddLiability] = useState(false);
  const [showAddEstate,    setShowAddEstate]    = useState(false);
  const [showLinkModal,    setShowLinkModal]    = useState(false);

  const assets      = netWorthData.assets      || [];
  const liabilities = netWorthData.liabilities || [];
  const history     = netWorthData.history     || [];
  const estate      = netWorthData.estatePlanning || [];

  // Linked accounts that act as assets (non-liability types)
  const linkedAssets = (linkedAccountsData || []).filter(a => !['credit_card', 'mortgage'].includes(a.type));
  const linkedLiabilities = (linkedAccountsData || []).filter(a => ['credit_card', 'mortgage'].includes(a.type));

  const totalAssets      = assets.reduce((s, a) => s + a.value, 0)
                         + linkedAssets.reduce((s, a) => s + (a.balance || 0), 0);
  const totalLiabilities = liabilities.reduce((s, l) => s + l.value, 0)
                         + linkedLiabilities.reduce((s, a) => s + (a.balance || 0), 0);
  const netWorth         = totalAssets - totalLiabilities;

  // ── Auto-snapshot on login (once per day) ─────────────────────────────────
  const snapshotRef = useRef(false);
  useEffect(() => {
    if (snapshotRef.current) return;
    if (netWorth === 0) return;
    const todayKey = new Date().toISOString().slice(0, 10);
    if (netWorthData.lastSnapshotDate === todayKey) return;
    snapshotRef.current = true;
    const newHistory = appendHistorySnapshot(history, netWorth);
    updateSection('net_worth', { ...netWorthData, history: newHistory, lastSnapshotDate: todayKey }, { silent: true });
  }, [netWorth]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissManualBanner = (accountId) => {
    const updated = (linkedAccountsData || []).map(a =>
      a.id === accountId ? { ...a, manualBannerDismissed: true } : a
    );
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
  };

  // Pie includes both manual assets and linked accounts grouped by type
  const pieGroups = {};
  assets.forEach(a => { pieGroups[a.type] = (pieGroups[a.type] || 0) + a.value; });
  linkedAssets.forEach(a => {
    const t = LINKED_TYPE_TO_PIE[a.type] || 'Cash';
    pieGroups[t] = (pieGroups[t] || 0) + (a.balance || 0);
  });
  const pieData = Object.entries(pieGroups).map(([name, value]) => ({ name, value }));

  // saveNW includes linked balances in the net worth history entry
  const saveNW = (newData) => {
    const newManualAssets = newData.assets ?? assets;
    const newManualLiabs  = newData.liabilities ?? liabilities;
    const newNetWorth = newManualAssets.reduce((s, a) => s + a.value, 0)
                      + linkedAssets.reduce((s, a) => s + (a.balance || 0), 0)
                      - newManualLiabs.reduce((s, l) => s + l.value, 0)
                      - linkedLiabilities.reduce((s, a) => s + (a.balance || 0), 0);
    const newHistory = appendHistorySnapshot(history, newNetWorth);
    updateSection('net_worth', { ...netWorthData, ...newData, history: newHistory });
  };

  const handleAddAsset = (asset) => {
    saveNW({ assets: [...assets, asset] });
  };

  const handleDeleteAsset = (idx) => {
    saveNW({ assets: assets.filter((_, i) => i !== idx) });
  };

  const handleAddLiability = (liability) => {
    saveNW({ liabilities: [...liabilities, liability] });
  };

  const handleDeleteLiability = (idx) => {
    saveNW({ liabilities: liabilities.filter((_, i) => i !== idx) });
  };

  const handleAddEstate = (item) => {
    updateSection('net_worth', { ...netWorthData, estatePlanning: [...estate, item] });
  };

  const handleDeleteEstate = (idx) => {
    updateSection('net_worth', { ...netWorthData, estatePlanning: estate.filter((_, i) => i !== idx) });
  };

  const missingEstate = estate.filter(e => e.status === 'missing').length;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {showAddAsset     && <AddAssetModal     onClose={() => setShowAddAsset(false)}     onAdd={handleAddAsset} />}
      {showAddLiability && <AddLiabilityModal onClose={() => setShowAddLiability(false)} onAdd={handleAddLiability} />}
      {showAddEstate    && <AddEstateModal    onClose={() => setShowAddEstate(false)}    onAdd={handleAddEstate} />}
      {showLinkModal    && <AccountLinkModal  onClose={() => setShowLinkModal(false)} context="networth" />}

      <div>
        <h1 className="text-2xl font-bold text-slate-100">Net Worth</h1>
        <p className="text-slate-400 text-sm mt-0.5">Asset breakdown, liabilities, and estate planning</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Assets',      value: fmt(totalAssets),      color: 'text-emerald-400' },
          { label: 'Total Liabilities', value: fmt(totalLiabilities), color: 'text-rose-400' },
          { label: 'Net Worth',         value: fmt(netWorth),         color: 'text-slate-100' },
        ].map(k => (
          <div key={k.label} className="bg-slate-800 border border-slate-700 rounded-xl p-5 text-center">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{k.label}</div>
            <div className={`text-2xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Charts -- only show when there's data */}
      {(assets.length > 0 || history.length > 1) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {history.length > 1 && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <h2 className="font-semibold text-slate-200 text-base mb-4">Trend</h2>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={history} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                    formatter={(v) => [fmt(v), 'Net Worth']}
                    labelStyle={{ color: '#cbd5e1' }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} fill="url(#nwGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {pieData.length > 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <h2 className="font-semibold text-slate-200 text-base mb-2">Asset Allocation</h2>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[entry.name] || '#64748b'} />
                    ))}
                  </Pie>
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    formatter={(value, entry) => (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>{value}: {fmt(entry.payload.value)}</span>
                    )}
                  />
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                    formatter={(v, n) => [fmt(v), n]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Assets & Liabilities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-200 text-base">Assets</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowLinkModal(true)}
                className="flex items-center gap-1.5 text-xs bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-300 px-3 py-1.5 rounded-lg transition-colors font-medium"
              >
                <Link2 size={13} /> Link Account
              </button>
              <button
                onClick={() => setShowAddAsset(true)}
                className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
              >
                <Plus size={13} /> Add Asset
              </button>
            </div>
          </div>
          {(assets.length === 0 && linkedAssets.length === 0) ? (
            <div className="text-center py-6">
              <p className="text-slate-500 text-sm mb-3">No assets added yet</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg transition-colors font-medium flex items-center gap-1.5"
                >
                  <Link2 size={12} /> Link Account
                </button>
                <span className="text-slate-600 text-xs">or</span>
                <button onClick={() => setShowAddAsset(true)} className="text-xs text-slate-400 hover:text-slate-300 underline">
                  add manually
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {/* Linked accounts (Plaid or manual) */}
                {linkedAssets.map(account => {
                  const p = totalAssets > 0 ? Math.round(((account.balance || 0) / totalAssets) * 100) : 0;
                  return (
                    <div key={account.id}>
                      {account.connectionType === 'manual' && !account.manualBannerDismissed && (
                        <div className="mb-2">
                          <ManualAccountBanner
                            account={account}
                            onDismiss={() => dismissManualBanner(account.id)}
                            onLinkPlaid={() => setShowLinkModal(true)}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="flex items-center gap-1.5 text-sm text-slate-300">
                              {account.name}
                              <ConnectionBadge account={account} />
                            </span>
                            <span className="text-sm font-medium text-slate-200">{fmt(account.balance || 0)}</span>
                          </div>
                          <div className="w-full bg-slate-700 rounded-full h-1">
                            <div className="h-1 rounded-full bg-blue-500" style={{ width: `${p}%` }} />
                          </div>
                        </div>
                        <span className="text-xs text-slate-500 w-8 text-right">{p}%</span>
                      </div>
                    </div>
                  );
                })}
                {/* Manually entered assets */}
                {assets.map((a, idx) => {
                  const p = totalAssets > 0 ? Math.round((a.value / totalAssets) * 100) : 0;
                  return (
                    <div key={idx} className="flex items-center gap-3 group">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-slate-300">{a.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-200">{fmt(a.value)}</span>
                            <button
                              onClick={() => handleDeleteAsset(idx)}
                              className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-rose-900/40 transition-all"
                            >
                              <Trash2 size={11} className="text-rose-400" />
                            </button>
                          </div>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1">
                          <div
                            className="h-1 rounded-full"
                            style={{ width: `${p}%`, background: PIE_COLORS[a.type] || '#64748b' }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 w-8 text-right">{p}%</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-700 flex justify-between">
                <span className="text-sm text-slate-400 font-medium">Total</span>
                <span className="text-sm font-bold text-emerald-400">{fmt(totalAssets)}</span>
              </div>
            </>
          )}
        </div>

        <div className="space-y-4">
          {/* Liabilities */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-200 text-base">Liabilities</h2>
              <button
                onClick={() => setShowAddLiability(true)}
                className="flex items-center gap-1.5 text-xs bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-300 px-3 py-1.5 rounded-lg transition-colors font-medium"
              >
                <Plus size={13} /> Add Debt
              </button>
            </div>
            {(liabilities.length === 0 && linkedLiabilities.length === 0) ? (
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm mb-1">No liabilities</p>
                <p className="text-xs text-slate-600">Debt-free or add mortgages, loans</p>
              </div>
            ) : (
              <>
                {/* Linked liability accounts (credit cards, mortgages) */}
                {linkedLiabilities.map(account => (
                  <div key={account.id} className="flex justify-between py-2 border-b border-slate-700/50 last:border-0">
                    <span className="flex items-center gap-1.5 text-sm text-slate-300">
                      {account.name}
                      <ConnectionBadge account={account} />
                    </span>
                    <span className="text-sm font-medium text-rose-400">{fmt(account.balance || 0)}</span>
                  </div>
                ))}
                {/* Manually entered liabilities */}
                {liabilities.map((l, idx) => (
                  <div key={idx} className="flex justify-between py-2 border-b border-slate-700/50 last:border-0 group">
                    <span className="text-sm text-slate-300">{l.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-rose-400">{fmt(l.value)}</span>
                      <button
                        onClick={() => handleDeleteLiability(idx)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-rose-900/40 transition-all"
                      >
                        <Trash2 size={11} className="text-rose-400" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between">
                  <span className="text-sm text-slate-400 font-medium">Total</span>
                  <span className="text-sm font-bold text-rose-400">{fmt(totalLiabilities)}</span>
                </div>
              </>
            )}
          </div>

          {/* Estate planning */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold text-slate-200 text-base">Estate Planning</h2>
              </div>
              <div className="flex items-center gap-2">
                {missingEstate > 0 && (
                  <span className="text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5">
                    {missingEstate} missing
                  </span>
                )}
                <button
                  onClick={() => setShowAddEstate(true)}
                  className="flex items-center gap-1 text-xs bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-300 px-2.5 py-1.5 rounded-lg transition-colors"
                >
                  <Plus size={12} /> Add
                </button>
              </div>
            </div>
            {estate.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm mb-1">No estate documents tracked</p>
                <p className="text-xs text-slate-600">Add your will, trust, POA, etc.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {estate.map((item, idx) => {
                  const s = ESTATE_STATUS[item.status] || ESTATE_STATUS.missing;
                  return (
                    <div key={idx} className="flex items-center gap-3 py-1.5 group">
                      {s.icon}
                      <span className="text-sm text-slate-300 flex-1">{item.item}</span>
                      <span className={`text-xs font-medium ${s.color}`}>{s.label}</span>
                      {item.date && (
                        <span className="text-xs text-slate-600">{new Date(item.date).getFullYear()}</span>
                      )}
                      <button
                        onClick={() => handleDeleteEstate(idx)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-rose-900/40 transition-all"
                      >
                        <Trash2 size={11} className="text-rose-400" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
